/**
 * Shared state for tracking the current authentication provider.
 * This module is used to avoid circular dependencies between
 * amplifyService and woningNetSsoService.
 *
 * The provider is persisted in sessionStorage so that after a full page reload,
 * WoningNet SSO users are still recognized and Amplify can be reconfigured
 * before the first auth check.
 */

export type AuthProvider = 'dvv' | 'woningnet';

const STORAGE_KEY = 'dvv_auth_provider';

// Track which auth provider is currently configured (in-memory)
let currentAuthProvider: AuthProvider = 'dvv';

/**
 * Get the current auth provider being used.
 */
export const getCurrentAuthProvider = (): AuthProvider => currentAuthProvider;

/**
 * Get the persisted auth provider from sessionStorage (if any).
 * Used on app init to restore WoningNet SSO state after a reload.
 */
export const getPersistedAuthProvider = (): AuthProvider | null => {
  if (typeof sessionStorage === 'undefined') {
    return null;
  }
  const stored = sessionStorage.getItem(STORAGE_KEY);
  if (stored === 'woningnet' || stored === 'dvv') {
    return stored;
  }
  return null;
};

/**
 * Set the current auth provider and persist WoningNet so it survives reloads.
 */
export const setCurrentAuthProvider = (provider: AuthProvider): void => {
  currentAuthProvider = provider;
  if (typeof sessionStorage === 'undefined') {
    return;
  }
  if (provider === 'woningnet') {
    sessionStorage.setItem(STORAGE_KEY, 'woningnet');
  } else {
    sessionStorage.removeItem(STORAGE_KEY);
  }
};

/**
 * Separate marker recording that this tab signed in with DVV, either through
 * a magic link or with a password.
 *
 * The auth provider above tracks which pool Amplify is *currently* configured
 * for and is reset on sign out, so it cannot answer "was this tab ever a DVV
 * tab?". That question matters for deep links that default to WoningNet SSO:
 * a DVV user whose session expired must be sent back to the DVV login rather
 * than to SSO.
 */
const DVV_LOGIN_KEY = 'dvv_login';

/**
 * Record that a DVV sign in succeeded in this tab.
 */
export const markDvvLogin = (): void => {
  if (typeof sessionStorage === 'undefined') {
    return;
  }
  sessionStorage.setItem(DVV_LOGIN_KEY, 'true');
};

/**
 * Whether a DVV sign in has happened in this tab.
 */
export const hasDvvLogin = (): boolean => {
  if (typeof sessionStorage === 'undefined') {
    return false;
  }
  return sessionStorage.getItem(DVV_LOGIN_KEY) === 'true';
};

/**
 * Forget the DVV marker, so a signed out user is routed by the rules that
 * apply to the link they open next.
 */
export const clearDvvLogin = (): void => {
  if (typeof sessionStorage === 'undefined') {
    return;
  }
  sessionStorage.removeItem(DVV_LOGIN_KEY);
};
